import React, { useState, useEffect } from "react";
import { Input } from "reactstrap";
import Datatable from "../../Datatable";
import Paginations from "../../Datatable/Paginations";
import { getBomHistory, getBomDetails, getDigiKeyBomEnrichment, getFile, shareFile } from "../../../actions";
import { connect } from "react-redux";
import { ImportIcon, EmailIcon } from "../../../constants/icons";
import "./historyStyle.css";
import { toaster } from "../../../function";
import { ToastsStore } from "react-toasts";
import { loadStructuredBomlinesForBom } from "../../../utils/loadStructuredBomlines";
import {
  buildHistoryExportPayload,
  downloadOriginalAndResultBomFiles,
  exportStructuredBomExcel,
} from "../../../utils/historyBomExport";

/** History dates are DD/MM/YYYY. `new Date("27/08/2026")` is invalid in Firefox. */
const historyDateTimestamp = (value) => {
  if (value == null || value === "") {
    return 0;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const text = String(value).trim();
  const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (dmy) {
    const day = Number(dmy[1]);
    const month = Number(dmy[2]);
    const year = Number(dmy[3]);
    return new Date(year, month - 1, day).getTime();
  }
  const parsed = Date.parse(text);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const HistoryTable = (props) => {
  const [bomList, setBomList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [exportingBomId, setExportingBomId] = useState(null);

  const buildRowExportPayload = async (row, exportLocally = true) => {
    const loaded = await loadStructuredBomlinesForBom({
      token: props.user.token,
      bomId: row.id,
      getBomDetails: props.getBomDetails,
      getFile: props.getFile,
      getDigiKeyBomEnrichment: props.getDigiKeyBomEnrichment,
    });

    return buildHistoryExportPayload({
      user: props.user,
      bomRow: row,
      bomDetails: loaded.bomDetails,
      structuredBomlines: loaded.structuredBomlines,
      columns: props.columns,
      freightTotal: loaded.freightTotal,
      tariffTotal: loaded.tariffTotal,
      exportLocally,
    });
  };

  const downloadHistoryBom = async (row) => {
    if (exportingBomId) {
      return;
    }

    setExportingBomId(row.id);
    try {
      const exportPayload = await buildRowExportPayload(row, true);
      await downloadOriginalAndResultBomFiles({
        user: props.user,
        getFile: props.getFile,
        filePath: row.file_path,
        fileName: row.file_name,
        resultFilePath: null,
        exportResult: exportPayload.structuredBomlines?.length
          ? exportPayload
          : null,
      });
    } catch (error) {
      toaster(ToastsStore, "error", "Could not export the BOM file.");
    } finally {
      setExportingBomId(null);
    }
  };

  const emailHistoryBom = async (row) => {
    if (exportingBomId) {
      return;
    }

    setExportingBomId(row.id);
    try {
      const exportPayload = await buildRowExportPayload(row, false);
      if (exportPayload.structuredBomlines?.length) {
        await exportStructuredBomExcel(exportPayload);
      }

      let data = await props.shareFile(
        props.user.token,
        row.result_file_path,
        "Result-" + row.file_name + ".xlsx"
      );
      if (data.status >= 200 && data.status < 400) {
        toaster(
          ToastsStore,
          "success",
          "Sent the BOM file to your registered email."
        );
      } else {
        data = await props.shareFile(
          props.user.token,
          row.file_path,
          row.file_name + ".xlsx"
        );
        if (data.status >= 200 && data.status < 400) {
          toaster(
            ToastsStore,
            "success",
            "Sent the BOM file to your registered email."
          );
        } else if (data.status === 404) {
          toaster(ToastsStore, "error", "Could not find the file.");
        } else {
          toaster(ToastsStore, "error", "Could not get the file.");
        }
      }
    } catch (error) {
      toaster(ToastsStore, "error", "Could not export the BOM file.");
    } finally {
      setExportingBomId(null);
    }
  };

  useEffect(() => {
    const getBoms = async () => {
      const boms = await props.getBomHistory(props.user.token);
      const sorted = Array.isArray(boms.data)
        ? [...boms.data]
            .map((row) => ({
              ...row,
              _ts: historyDateTimestamp(row.date_of_upload || row.created_at),
            }))
            .sort((a, b) => (b._ts || 0) - (a._ts || 0) || (b.id || 0) - (a.id || 0))
        : boms.data;
      setBomList(sorted);
      setIsLoading(false);
    };

    getBoms();
  }, [props.user.token]);

  const importBom = async (row) => {
    const fileBlob = await props.getFile(props.user.token, row.file_path);
    fileBlob.name = row.file_name + ".xlsx";
    props.fileUpload([fileBlob]);
  };

  const column = [
    {
      fieldName: "file_name",
      label: "File Name",
      sort: true,
      customRow: (row) =>
        props.isHistoryPage ? (
          <a href={`/history/${row.id}`}>{row.file_name}</a>
        ) : (
          <span className="clickableSpan" onClick={() => importBom(row)}>
            {row.file_name}
          </span>
        ),
    },
    {
      fieldName: "customer_name",
      label: "Customer Name",
      sort: true,
      customRow: (row) => <span>{row.customer_name ?? "N/A"}</span>,
    },
    {
      fieldName: "part_rev",
      label: "Part REV#",
      sort: true,
      customRow: (row) => <span>{row.part_rev ?? "N/A"}</span>,
    },
    {
      fieldName: "date_of_upload",
      sortField: "_ts",
      label: "Date of upload",
      sort: true,
      customRow: (row) => <span>{row.date_of_upload}</span>,
    },
    {
      fieldName: "created_by",
      label: "Created By",
      sort: true,
      customRow: (row) => <span>{row.created_by}</span>,
    },
    props.isHistoryPage
      ? {
          fieldName: "action",
          label: "Action",
          sort: true,
          customRow: (row) => (
            <React.Fragment>
              <span className="historyEmailIcon">
                <EmailIcon
                  className="mr-2"
                  cursor={"pointer"}
                  onClick={() => emailHistoryBom(row)}
                />
              </span>
              <span className="historyDownloadIcon">
                <ImportIcon
                  className="mr-2"
                  cursor={"pointer"}
                  onClick={() => downloadHistoryBom(row)}
                />
              </span>
            </React.Fragment>
          ),
        }
      : {},
  ];

  const [searchText, setSearchText] = useState(null);
  const handleChange = (e) => {
    setSearchText(e.target.value);
    setPageActive(1);
  };

  const [showEntry] = useState(10);
  const [pageActive, setPageActive] = useState(1);
  const [totalRecords, setTotalRecords] = useState(bomList.length);

  const handlePagination = (e) => {
    setPageActive(e);
  };

  const changeTableParams = (total, page) => {
    if (page) setPageActive(page);
    setTotalRecords(total);
  };

  return (
    <React.Fragment>
      <div className="p-20">
        <div className="card">
          <div className="history cardHeader">
            <h3>
              History
              <small className="d-block">Total - {bomList?.length ?? 0}</small>
            </h3>
            <div className="history searchBox">
              <Input
                type="text"
                placeholder="Search"
                name="searchText"
                onChange={(e) => handleChange(e)}
              />
            </div>
          </div>
          <Datatable
            data={bomList}
            columns={column}
            pageActive={pageActive}
            showEntry={showEntry}
            searchText={searchText}
            changeTableParams={changeTableParams}
            defaultSortField="_ts"
            defaultSortType="desc"
            loading={isLoading}
          />
        </div>
        {totalRecords > showEntry && (
          <Paginations
            totalRecords={totalRecords}
            showEntry={showEntry}
            pageActive={pageActive}
            click={handlePagination}
            prevClick={() => setPageActive(parseInt(pageActive) - 1)}
            nextClick={() => setPageActive(parseInt(pageActive) + 1)}
          />
        )}
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state) => ({ user: state.user });
export default connect(mapStateToProps, {
  getBomHistory,
  getFile,
  shareFile,
  getBomDetails,
  getDigiKeyBomEnrichment,
})(HistoryTable);
